import { checkCellValueStyle, formatPercentage } from '@toggle/helpers';
import { Episode, periods } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HeaderRow } from './components/header-row/HeaderRow';
import * as S from './PriceMovementTable.styles';

export interface PriceMovementTableProps {
  episodes: Episode[];
  isFixedIncome?: boolean;
}

interface MappedEpisode extends Record<string, number> {
  best: number;
  upside: number;
  median: number;
  downside: number;
  worst: number;
}

export const PriceMovementTable = ({
  episodes,
  isFixedIncome = false,
}: PriceMovementTableProps) => {
  const tradingRangeLabel = ['best', 'upside', 'median', 'downside', 'worst'];

  const { t } = useTranslation('widget');
  const suffix = isFixedIncome ? 'bps' : '%';
  const decimals = isFixedIncome ? 0 : 2;

  const headerLabels = periods.map(period => {
    /* i18next-extract-mark-context-next-line  ["1w", "2w", "1m", "3m", "6m"] */
    return t('widget:priceMovement.horizon', {
      context: period,
    });
  });

  const priceMovementReference = useMemo((): Record<string, MappedEpisode> => {
    return episodes.reduce((priceRecord, episode) => {
      const { period, best, upside, median, downside, worst } = episode;
      return {
        ...priceRecord,
        [period]: { best, upside, median, downside, worst },
      };
    }, {});
  }, [episodes]);

  const MovementBodyRow = () => {
    return tradingRangeLabel.map((label, labelIndex) => {
      const periodRows = periods.map((period, periodIndex) => {
        const periodValue = priceMovementReference[period];

        return (
          <S.MovementBodyCell
            key={`body-${label}-${period}`}
            $numericalStyle={checkCellValueStyle(periodValue?.[label])}
            $index={[labelIndex, periodIndex]}
          >
            {periodValue?.[label]
              ? `${formatPercentage(periodValue[label], {
                  suffix,
                  decimals,
                })}`
              : '-'}
          </S.MovementBodyCell>
        );
      });

      periodRows.push(
        <S.MovementColumnLabel
          key={`body-${label}-${labelIndex}`}
          $numericalStyle={2}
          $index={[labelIndex, periods.length + 1]}
        >
          {
            /* i18next-extract-mark-context-next-line  ["best", "upside", "median", "downside", "worst"] */
            t(`widget:priceMovement.summary`, {
              context: label,
            })
          }
        </S.MovementColumnLabel>
      );

      return <tr key={label}>{periodRows}</tr>;
    });
  };

  return (
    <S.Container>
      <S.MovementTable
        withBodyRowHover={false}
        TableHeaderContent={<HeaderRow labels={headerLabels} />}
        TableBodyContent={MovementBodyRow()}
      />
      <>
        <S.BackgroundVector />
        <S.Label>{t('widget:priceMovement.typicalTradingRange')}</S.Label>
      </>
    </S.Container>
  );
};
